import React, { useCallback, useMemo } from "react"
import { Box, ToggleButtonGroup, useTheme } from "@mui/material"
import { useNavigate } from "react-router-dom"

import {
  CURRENT_EVENT_FILTER_VALUE,
  Q_PROMOTION_GROUP,
  UPCOMING_EVENT_FILTER_VALUE,
} from "constant"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { usePromotion } from "hooks/promotion/usePromotion"

import { PageCard, PageCardContent } from "components/card"
import { PercentIcon } from "components/icons"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { VenueSelector } from "components/VenueSelector"
import { Carousel, CarouselItem } from "components/Carousel"
import { PromotionCard } from "components/domains/promotions"
import { EmptyData } from "components/EmptyData"
import { CategoryFilterChip } from "components/CategoryFilterBar"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { SidebarMenu } from "components/navigation/SidebarMenu"

export const PromotionsKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    venues,
    isMultiVenueProject,
    locationVenueId,
    venueFilter,
    changeVenueFilter,
    groupPromotions,
    activeGroupPromotion,
    handleChange,
  } = usePromotion()
  const theme = useTheme()
  const activePromotions = groupPromotions[activeGroupPromotion]
  const slidePerPage = 3
  const carouselType =
    activePromotions && activePromotions.length < slidePerPage
      ? "slide"
      : "loop"
  const autoplay = activePromotions && activePromotions.length > 2

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.promotions) || <PercentIcon />,
    [theme]
  )

  const { venueSelector } = theme.components[`AppMap`]?.styleOverrides || {}

  const { styleOverrides } = theme.components?.[`PromotionPage`] || {}
  const { promotionCarouselOption } =
    theme.components[`PromotionPage`]?.defaultProps || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    promotionCard,
    emptyDataWrapper,
    promotionGroupSelectorButton,
  } = styleOverrides

  const handleClickSlide = useCallback(
    (_splide, { slide }, _event) => {
      navigate(slide.getAttribute("link"))
    },
    [navigate]
  )

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          <Box sx={carouselContainer}>
            {activePromotions && activePromotions.length > 0 ? (
              <Carousel
                onClick={handleClickSlide}
                options={{
                  type: carouselType,
                  pagination: true,
                  focus: "center",
                  trimSpace: false,
                  autoplay,
                  gap: "0",
                  ...promotionCarouselOption,
                }}
                sx={carousel}
              >
                {activePromotions.map((promotion) => {
                  const sponsoredId = promotion?.properties?.sponsoredId || ""
                  return (
                    <CarouselItem
                      link={`/promotions/${promotion.id}?${Q_PROMOTION_GROUP}=${activeGroupPromotion}`}
                      key={`${sponsoredId}${promotion.id}`}
                    >
                      <PromotionCard promotion={promotion} sx={promotionCard} />
                    </CarouselItem>
                  )
                })}
              </Carousel>
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  primary={t("No Promotion")}
                  secondary={t("Check Back Soon")}
                />
              </Box>
            )}
          </Box>

          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}

          <Box sx={promotionGroupSelectorButton}>
            <ToggleButtonGroup
              value={activeGroupPromotion}
              onChange={handleChange}
              exclusive
            >
              <CategoryFilterChip value={CURRENT_EVENT_FILTER_VALUE}>
                {t("Current Promotions")}
              </CategoryFilterChip>
              <CategoryFilterChip value={UPCOMING_EVENT_FILTER_VALUE}>
                {t("Upcoming Promotions")}
              </CategoryFilterChip>
            </ToggleButtonGroup>
          </Box>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
